import type { Availability, DayIntensity } from '@/Common/constants';

/** The intensity the system chooses for a day: High where the riding history makes it a high intensity day (usual.high), else Either. */
export const usualIntensity = (usual?: { high?: boolean } | null): DayIntensity => usual?.high ? 'high' : 'advisor';

/**
 * Whether the system chooses everything about the day: the duration, the time, adjusting progressively, and the intensity,
 * which is the day's usual intensity (usualIntensity). A day left at Either, as before a history-high day was stored as High,
 * counts too.
 */
export const isSystemChosen = (a: Availability, usual: DayIntensity = 'advisor'): boolean =>
  (a.duration ?? -1) <= 0 && a.time === 'advisor' && !a.fixed_availability
  && ['advisor', usual].includes(a.intensity ?? 'advisor');

/** An availability as the calendar settings keep it, so that two can be compared: a time without seconds, no duration as -1. */
export const normalizeAvailability = (a: Availability): Availability => ({
  availability: !!a.availability,
  time: !a.time || a.time === 'advisor' ? 'advisor' : a.time.slice(0, 5),
  duration: a.duration == 999 ? 5 * 60 : (a.duration ?? -1) <= 0 ? -1 : a.duration,
  fixed_availability: !!a.fixed_availability,
  intensity: a.availability ? (a.intensity ?? 'advisor') : 'advisor',
});

/**
 * Two days that come to the same thing: unavailable days are the same whatever else they hold, and so are days the system
 * chooses everything for (their intensity is the usual, whether stored as that or as Either).
 */
export const sameAvailability = (a: Availability, b: Availability, usual: DayIntensity = 'advisor'): boolean => {
  const canon = (d: Availability) => { const n = normalizeAvailability(d); return isSystemChosen(n, usual) ? { ...n, intensity: usual } : n; };
  const x = canon(a), y = canon(b);
  if (!x.availability && !y.availability) return true;
  return x.availability === y.availability && x.time === y.time && x.duration === y.duration
    && x.fixed_availability === y.fixed_availability && x.intensity === y.intensity;
};
